import React, { useState, useEffect } from 'react';
import { 
    LuSparkles, 
    LuPalette, 
    LuSearch, 
    LuShoppingCart, 
    LuCheck, 
    LuClock, 
    LuMail, 
    LuExternalLink, 
    LuSettings, 
    LuShield,
    LuShieldCheck, 
    LuX, 
    LuArrowRight, 
    LuSlidersHorizontal,
    LuSend,
    LuCrown,
    LuBuilding2,
    LuCode,
    LuGlobe,
    LuCircleCheck
} from 'react-icons/lu';

/**
 * Default Agency Upsell Configuration Defaults
 */
export const DEFAULT_AGENCY_UPSELL_CONFIG = {
    enabled: true,
    resellerMode: false,
    agencyName: 'Mad Curious',
    expertTitle: 'Cleverologist',
    buttonLabel: 'Hire a Cleverologist',
    contactEmail: 'cleverologist@madcurious.com',
    bookingUrl: 'https://madcurious.com/',
    portfolioUrl: 'https://madcurious.com/',
    tagline: 'Programmatic Design Sprints & Turnkey Assistance',
    description: 'Transform your website with high-touch agency support. Whether you need custom CSS polish, an exhaustive SEO audit, or a complex store migration, our dedicated specialists deliver turnkey results.',
    contextualTriggers: {
        enabled: true,
        cssTrigger: true,
        seoTrigger: true,
        taxTrigger: true
    },
    packages: [
        {
            id: 'site_polish',
            name: 'Site Polish Package',
            price: '$199–$499',
            priceNumeric: 299,
            idealFor: 'Design review, mobile polish & typography',
            description: 'A designer reviews layout, mobile responsiveness, visual hierarchy, and typography polish.',
            turnaround: '24–48 Hours',
            badge: 'MOST POPULAR',
            badgeColor: '#10b981',
            contextualTag: 'css_design',
            enabled: true,
            inclusions: [
                'Full desktop & mobile layout audit',
                'Custom typography & visual hierarchy tune-up',
                'Mobile viewport responsiveness fixes',
                'Hero section aesthetic polish',
                'Post-sprint video walkthrough'
            ]
        },
        {
            id: 'seo_speed_audit',
            name: 'SEO & Speed Audit',
            price: '$150',
            priceNumeric: 150,
            idealFor: 'Search rankings, Core Web Vitals, Schema',
            description: 'Schema markup setup, local SEO optimization, meta tag audit, and performance tuning.',
            turnaround: '24 Hours',
            badge: 'HIGH ROI',
            badgeColor: '#3b82f6',
            contextualTag: 'seo',
            enabled: true,
            inclusions: [
                'Structured JSON-LD Schema markup setup',
                'Title & meta description optimization',
                'Core Web Vitals & PageSpeed audit',
                'OpenGraph & Twitter social card verification',
                'Search Console indexing checklist'
            ]
        },
        {
            id: 'store_migration',
            name: 'Store Migration',
            price: '$299',
            priceNumeric: 299,
            idealFor: 'Moving from Shopify, Etsy, WooCommerce',
            description: 'Moving an existing store from Shopify/Etsy over to OMNIVASO with zero catalog loss.',
            turnaround: '2–3 Days',
            badge: 'ZERO HEADACHE',
            badgeColor: '#8b5cf6',
            contextualTag: 'ecommerce_tax',
            enabled: true,
            inclusions: [
                'Product catalog & variant export/import',
                'High-res imagery & gallery transfer',
                'Category & collection structuring',
                'Payment gateway & tax setup verification',
                'Cart checkout test run & cutover guidance'
            ]
        }
    ],
    leadsHistory: []
};

/**
 * Resolves active agency upsell configuration merged with defaults
 */
export const getAgencyUpsellConfig = (config) => {
    const raw = config?.modules?.agencyUpsell || {};
    return {
        ...DEFAULT_AGENCY_UPSELL_CONFIG,
        ...raw,
        contextualTriggers: {
            ...DEFAULT_AGENCY_UPSELL_CONFIG.contextualTriggers,
            ...(raw.contextualTriggers || {})
        },
        packages: Array.isArray(raw.packages) && raw.packages.length > 0 
            ? raw.packages 
            : DEFAULT_AGENCY_UPSELL_CONFIG.packages,
        leadsHistory: Array.isArray(raw.leadsHistory) 
            ? raw.leadsHistory 
            : []
    };
};

/**
 * In-Dashboard Contextual Trigger Banner
 */
export const ContextualAgencyTrigger = ({ 
    context = 'css_design', 
    config, 
    compact = false, 
    onOpenSprint 
}) => {
    const upsell = getAgencyUpsellConfig(config);
    if (!upsell.enabled || !upsell.contextualTriggers?.enabled) return null;

    if (context === 'css_design' && !upsell.contextualTriggers?.cssTrigger) return null;
    if (context === 'seo' && !upsell.contextualTriggers?.seoTrigger) return null;
    if (context === 'ecommerce_tax' && !upsell.contextualTriggers?.taxTrigger) return null;

    const targetPackage = upsell.packages.find(p => p.contextualTag === context && p.enabled) || upsell.packages[0];
    if (!targetPackage) return null;

    const handleTriggerClick = () => {
        if (typeof onOpenSprint === 'function') {
            onOpenSprint(targetPackage.id);
        } else {
            window.dispatchEvent(new CustomEvent('open-agency-sprint', { 
                detail: { packageId: targetPackage.id } 
            }));
        }
    };

    const isCss = context === 'css_design';
    const isSeo = context === 'seo';
    const isTax = context === 'ecommerce_tax';

    const bgGradient = isCss 
        ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)'
        : isSeo 
        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)'
        : 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(239, 68, 68, 0.08) 100%)';

    const borderColor = isCss ? '#e879f9' : isSeo ? '#60a5fa' : '#f59e0b';
    const iconColor = isCss ? '#c026d3' : isSeo ? '#2563eb' : '#d97706';

    if (compact) {
        return (
            <button
                type="button"
                onClick={handleTriggerClick}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: bgGradient,
                    border: `1px solid ${borderColor}`,
                    color: '#0f172a',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.15s ease'
                }}
                title={`${upsell.buttonLabel}: ${targetPackage.name} (${targetPackage.price})`}
            >
                <LuSparkles size={14} style={{ color: iconColor }} />
                <span>{upsell.buttonLabel} ({targetPackage.price})</span>
            </button>
        );
    }

    return (
        <div style={{
            background: bgGradient,
            border: `1px solid ${borderColor}`,
            borderRadius: '12px',
            padding: '14px 18px',
            margin: '12px 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 280px' }}>
                <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    flexShrink: 0
                }}>
                    {isCss && <LuPalette size={20} style={{ color: iconColor }} />}
                    {isSeo && <LuSearch size={20} style={{ color: iconColor }} />}
                    {isTax && <LuShoppingCart size={20} style={{ color: iconColor }} />}
                </div>
                <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>Struggling with {isCss ? 'CSS & Layouts?' : isSeo ? 'SEO & Core Web Vitals?' : 'Taxes or Shopify Migration?'}</span>
                        <span style={{
                            background: borderColor,
                            color: '#ffffff',
                            fontSize: '0.68rem',
                            fontWeight: '900',
                            padding: '2px 7px',
                            borderRadius: '10px'
                        }}>
                            {targetPackage.price}
                        </span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px', lineHeight: 1.4 }}>
                        {isCss && `Get the Site Polish Package (${targetPackage.price}): A senior designer reviews layout, mobile responsiveness, and typography.`}
                        {isSeo && `Get the SEO & Speed Audit (${targetPackage.price}): Schema markup setup, local SEO optimization, and meta tag audit.`}
                        {isTax && `Get Store Migration (${targetPackage.price}): Move your catalog, customer data, and tax settings from Shopify/Etsy seamlessly.`}
                    </div>
                </div>
            </div>

            <button
                type="button"
                onClick={handleTriggerClick}
                style={{
                    background: isCss ? '#a21caf' : isSeo ? '#1d4ed8' : '#b45309',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    transition: 'all 0.15s ease'
                }}
            >
                <LuSparkles size={14} />
                <span>{upsell.buttonLabel}</span>
            </button>
        </div>
    );
};

/**
 * Standalone In-Dashboard "Hire a Cleverologist" Header/Sidebar Button
 */
export const HireCleverologistButton = ({ config, onOpen }) => {
    const upsell = getAgencyUpsellConfig(config);
    if (!upsell.enabled) return null;

    const handleClick = () => {
        if (typeof onOpen === 'function') {
            onOpen();
        } else {
            window.dispatchEvent(new CustomEvent('open-agency-sprint', { detail: {} }));
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
                color: '#ffffff',
                border: '1px solid rgba(129, 140, 248, 0.4)',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '0.84rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(49, 46, 129, 0.25)',
                transition: 'all 0.2s ease'
            }}
            title={`Access ${upsell.agencyName} ${upsell.expertTitle} services and sprint packages`}
        >
            <span style={{
                background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                borderRadius: '6px',
                padding: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <LuSparkles size={12} color="#ffffff" />
            </span>
            <span>{upsell.buttonLabel}</span>
        </button>
    );
};

/**
 * Interactive Client Booking / Sprint Inquiry Modal
 */
export const AgencyBookingModal = ({ 
    isOpen, 
    onClose, 
    config, 
    initialPackageId = 'site_polish', 
    onSaveLead 
}) => {
    const upsell = getAgencyUpsellConfig(config);
    const [selectedPackageId, setSelectedPackageId] = useState(initialPackageId);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState(() => {
        if (typeof window !== 'undefined') return window.location.origin;
        return '';
    });
    const [notes, setNotes] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');

    useEffect(() => {
        if (initialPackageId) setSelectedPackageId(initialPackageId);
    }, [initialPackageId]);

    if (!isOpen) return null;

    const selectedPkg = upsell.packages.find(p => p.id === selectedPackageId) || upsell.packages[0];

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setStatusMsg('Please provide a contact email address.');
            return;
        }

        const newLead = {
            id: `lead_${Date.now()}`,
            packageId: selectedPackageId,
            packageName: selectedPkg?.name,
            packagePrice: selectedPkg?.price,
            name: name.trim() || 'Client',
            email: email.trim(),
            websiteUrl: websiteUrl.trim(),
            notes: notes.trim(),
            createdAt: new Date().toISOString(),
            status: 'new'
        };

        // Persist lead in local history
        try {
            const existingLeads = JSON.parse(localStorage.getItem('omnivaso_agency_leads') || '[]');
            existingLeads.unshift(newLead);
            localStorage.setItem('omnivaso_agency_leads', JSON.stringify(existingLeads));
        } catch (err) {}

        if (typeof onSaveLead === 'function') {
            onSaveLead(newLead);
        }

        setIsSubmitted(true);
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999999,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: 'Inter, system-ui, sans-serif'
        }}>
            <div style={{
                background: '#ffffff',
                width: '100%',
                maxWidth: '720px',
                borderRadius: '20px',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '90vh'
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    color: '#ffffff',
                    padding: '24px 28px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '16px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <div>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'rgba(139, 92, 246, 0.25)',
                            color: '#c4b5fd',
                            border: '1px solid rgba(139, 92, 246, 0.4)',
                            borderRadius: '16px',
                            padding: '3px 12px',
                            fontSize: '0.75rem',
                            fontWeight: '800',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            marginBottom: '8px'
                        }}>
                            <LuSparkles size={12} />
                            <span>{upsell.agencyName} • {upsell.expertTitle} Services</span>
                        </div>
                        <h2 style={{ margin: 0, fontSize: '1.45rem', fontWeight: '800', color: '#ffffff' }}>
                            {upsell.buttonLabel}
                        </h2>
                        <p style={{ margin: '6px 0 0 0', fontSize: '0.85rem', color: '#94a3b8', lineHeight: 1.5 }}>
                            Select a turnkey one-time sprint or tell us what you're stuck on. Fast turnaround guaranteed.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            color: '#ffffff',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <LuX size={18} />
                    </button>
                </div>

                {/* Body Content */}
                <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                    {isSubmitted ? (
                        <div style={{ textAlign: 'center', padding: '36px 12px' }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: '#dcfce7',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px auto'
                            }}>
                                <LuCircleCheck size={36} />
                            </div>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.35rem', color: '#0f172a', fontWeight: '800' }}>
                                Sprint Inquiry Received!
                            </h3>
                            <p style={{ color: '#64748b', fontSize: '0.92rem', maxWidth: '480px', margin: '0 auto 20px auto', lineHeight: 1.6 }}>
                                Thank you, <strong>{name || 'Friend'}</strong>! A {upsell.expertTitle} from <strong>{upsell.agencyName}</strong> has been notified regarding your request for the <strong>{selectedPkg?.name}</strong>.
                            </p>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {upsell.bookingUrl && (
                                    <a
                                        href={upsell.bookingUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            background: '#2563eb',
                                            color: '#ffffff',
                                            textDecoration: 'none',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            fontWeight: '700',
                                            fontSize: '0.88rem',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <span>Open Calendar / Booking Link</span>
                                        <LuExternalLink size={14} />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    style={{
                                        background: '#f1f5f9',
                                        color: '#334155',
                                        border: '1px solid #cbd5e1',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        fontWeight: '700',
                                        fontSize: '0.88rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Close Window
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {/* Sprint Package Selector */}
                            <div style={{ marginBottom: '22px' }}>
                                <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '10px' }}>
                                    Select One-Time Design Sprint:
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                    {upsell.packages.map((pkg) => {
                                        const isSelected = pkg.id === selectedPackageId;
                                        return (
                                            <div
                                                key={pkg.id}
                                                onClick={() => setSelectedPackageId(pkg.id)}
                                                style={{
                                                    border: `2px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`,
                                                    background: isSelected ? '#eff6ff' : '#ffffff',
                                                    borderRadius: '12px',
                                                    padding: '14px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    position: 'relative'
                                                }}
                                            >
                                                {pkg.badge && (
                                                    <span style={{
                                                        position: 'absolute',
                                                        top: '-8px',
                                                        right: '12px',
                                                        background: pkg.badgeColor || '#3b82f6',
                                                        color: '#ffffff',
                                                        fontSize: '0.65rem',
                                                        fontWeight: '900',
                                                        padding: '2px 6px',
                                                        borderRadius: '8px'
                                                    }}>
                                                        {pkg.badge}
                                                    </span>
                                                )}
                                                <div style={{ fontSize: '0.92rem', fontWeight: '800', color: isSelected ? '#1d4ed8' : '#0f172a' }}>
                                                    {pkg.name}
                                                </div>
                                                <div style={{ fontSize: '1.15rem', fontWeight: '900', color: '#0f172a', margin: '4px 0' }}>
                                                    {pkg.price}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4 }}>
                                                    {pkg.idealFor}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Deliverables summary */}
                            {selectedPkg && (
                                <div style={{
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '10px',
                                    padding: '14px 16px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#0f172a' }}>
                                            Includes in {selectedPkg.name}:
                                        </span>
                                        <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <LuClock size={12} /> {selectedPkg.turnaround}
                                        </span>
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.8rem', color: '#475569', lineHeight: 1.5 }}>
                                        {selectedPkg.inclusions?.map((inc, i) => (
                                            <li key={i}>{inc}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Contact Form Inputs */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                                <div>
                                    <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                        Your Name
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Alex Miller"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                        Contact Email *
                                    </label>
                                    <input
                                        type="email"
                                        required
                                        placeholder="alex@example.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    Site URL / Subdomain
                                </label>
                                <input
                                    type="text"
                                    placeholder="https://mysite.omnivaso.com"
                                    value={websiteUrl}
                                    onChange={e => setWebsiteUrl(e.target.value)}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                />
                            </div>

                            <div style={{ marginBottom: '18px' }}>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    Project Notes or Struggle Area (Optional)
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="Tell us what you'd like polished, specific mobile viewport bugs, Shopify catalog details, or SEO goals..."
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem', resize: 'vertical' }}
                                />
                            </div>

                            {statusMsg && (
                                <div style={{ color: '#ef4444', fontSize: '0.82rem', marginBottom: '12px', fontWeight: '600' }}>
                                    {statusMsg}
                                </div>
                            )}

                            {/* Submit Button */}
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    style={{
                                        background: 'transparent',
                                        color: '#64748b',
                                        border: '1px solid #cbd5e1',
                                        padding: '10px 18px',
                                        borderRadius: '8px',
                                        fontWeight: '700',
                                        fontSize: '0.88rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '10px 22px',
                                        borderRadius: '8px',
                                        fontWeight: '800',
                                        fontSize: '0.92rem',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
                                    }}
                                >
                                    <LuSend size={15} />
                                    <span>Request {selectedPkg?.name} ({selectedPkg?.price})</span>
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Main Agency Upsell Module Manager for Dashboard
 * Provides both Client Sprint Catalog and White-Label Reseller Controls
 */
export const AgencyUpsellManager = ({ config, onSave, isSuperUser: propIsSuperUser }) => {
    const [data, setData] = useState(config || {});
    const [activeSubTab, setActiveSubTab] = useState('catalog'); // 'catalog' | 'reseller_config' | 'leads'
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [modalPackageId, setModalPackageId] = useState('site_polish');
    const [saveStatus, setSaveStatus] = useState('');

    const loggedInRole = (localStorage.getItem('userRole') || '').toLowerCase();
    const isSuperUser = propIsSuperUser !== undefined ? propIsSuperUser : (loggedInRole === 'superuser');

    // Security check: clients can only ever access the client-facing catalog
    useEffect(() => {
        if (!isSuperUser && activeSubTab !== 'catalog') {
            setActiveSubTab('catalog');
        }
    }, [isSuperUser, activeSubTab]);

    useEffect(() => {
        if (config) setData(config);
    }, [config]);

    const upsell = getAgencyUpsellConfig(data);

    const updateUpsell = (partial) => {
        const currentModules = data.modules || {};
        const updatedUpsell = {
            ...upsell,
            ...partial
        };
        const newData = {
            ...data,
            modules: {
                ...currentModules,
                agencyUpsell: updatedUpsell
            }
        };
        setData(newData);
        if (typeof onSave === 'function') {
            onSave(newData);
        }
        setSaveStatus('✅ Settings updated');
        setTimeout(() => setSaveStatus(''), 2500);
    };

    const updatePackage = (index, field, value) => {
        const newPkgs = [...upsell.packages];
        newPkgs[index] = { ...newPkgs[index], [field]: value };
        updateUpsell({ packages: newPkgs });
    };

    const handleOpenBooking = (pkgId) => {
        setModalPackageId(pkgId);
        setIsBookingModalOpen(true);
    };

    const handleSaveLeadFromModal = (lead) => {
        const currentLeads = Array.isArray(upsell.leadsHistory) ? [...upsell.leadsHistory] : [];
        currentLeads.unshift(lead);
        updateUpsell({ leadsHistory: currentLeads });
    };

    return (
        <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            {/* Top Overview Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
                color: '#ffffff',
                border: '1px solid rgba(129, 140, 248, 0.25)',
                borderRadius: '16px',
                padding: '28px 24px',
                marginBottom: '24px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px'
            }}>
                <div>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: isSuperUser ? 'rgba(139, 92, 246, 0.35)' : 'rgba(59, 130, 246, 0.2)',
                        border: isSuperUser ? '1px solid rgba(167, 139, 250, 0.5)' : '1px solid rgba(59, 130, 246, 0.3)',
                        color: isSuperUser ? '#c4b5fd' : '#93c5fd',
                        borderRadius: '20px',
                        padding: '4px 12px',
                        fontSize: '0.78rem',
                        fontWeight: '800',
                        letterSpacing: '0.05em',
                        marginBottom: '8px'
                    }}>
                        {isSuperUser ? <LuShield size={14} /> : <LuCrown size={14} />}
                        <span>
                            {isSuperUser 
                                ? '⚡ AGENCY SUPER USER • WHITE-LABEL IDENTITY CONTROL' 
                                : `${upsell.agencyName.toUpperCase()} PROFESSIONAL SERVICES`
                            }
                        </span>
                    </div>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '800', color: '#ffffff' }}>
                        {isSuperUser 
                            ? `${upsell.agencyName} White-Label Identity & Upsell Engine`
                            : `${upsell.agencyName} Professional Design Sprints`
                        }
                    </h3>
                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem', maxWidth: '640px', lineHeight: 1.5 }}>
                        {isSuperUser
                            ? 'Configure your white-label agency branding, custom titles, package pricing markup, and lead routing. These settings are completely hidden from client accounts.'
                            : 'Request packaged one-time design sprints, custom typography reviews, and platform migrations directly from certified Cleverologists.'
                        }
                    </p>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        type="button"
                        onClick={() => handleOpenBooking('site_polish')}
                        style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: '#ffffff',
                            border: 'none',
                            padding: '10px 18px',
                            borderRadius: '8px',
                            fontWeight: '700',
                            fontSize: '0.88rem',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 8px rgba(37,99,235,0.3)'
                        }}
                    >
                        <LuSparkles size={14} />
                        <span>{upsell.buttonLabel}</span>
                    </button>
                </div>
            </div>

            {/* Navigation Sub-Tabs — Only rendered if Super User has multiple tabs available */}
            {isSuperUser && (
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    borderBottom: '1px solid #e2e8f0',
                    marginBottom: '24px',
                    paddingBottom: '2px'
                }}>
                    <button
                        type="button"
                        onClick={() => setActiveSubTab('catalog')}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            borderBottom: `3px solid ${activeSubTab === 'catalog' ? '#2563eb' : 'transparent'}`,
                            color: activeSubTab === 'catalog' ? '#1e293b' : '#64748b',
                            padding: '10px 18px',
                            fontSize: '0.92rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <LuSparkles size={16} />
                        <span>Packaged Design Sprints</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveSubTab('reseller_config')}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            borderBottom: `3px solid ${activeSubTab === 'reseller_config' ? '#7c3aed' : 'transparent'}`,
                            color: activeSubTab === 'reseller_config' ? '#5b21b6' : '#64748b',
                            padding: '10px 18px',
                            fontSize: '0.92rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <LuSlidersHorizontal size={16} />
                        <span>White-Label Agency Identity &amp; Controls</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveSubTab('leads')}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            borderBottom: `3px solid ${activeSubTab === 'leads' ? '#2563eb' : 'transparent'}`,
                            color: activeSubTab === 'leads' ? '#1e293b' : '#64748b',
                            padding: '10px 18px',
                            fontSize: '0.92rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <LuMail size={16} />
                        <span>Sprint Leads ({upsell.leadsHistory?.length || 0})</span>
                    </button>
                </div>
            )}

            {saveStatus && (
                <div style={{ marginBottom: '16px', color: '#16a34a', fontWeight: '700', fontSize: '0.85rem' }}>
                    {saveStatus}
                </div>
            )}

            {/* TAB 1: SPRINT PACKAGES CATALOG (CLIENT FACING VIEW) */}
            {activeSubTab === 'catalog' && (
                <div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '24px',
                        alignItems: 'stretch'
                    }}>
                        {upsell.packages.map((pkg) => (
                            <div
                                key={pkg.id}
                                style={{
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '16px',
                                    padding: '24px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    position: 'relative',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                    opacity: pkg.enabled ? 1 : 0.6
                                }}
                            >
                                {pkg.badge && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-10px',
                                        left: '20px',
                                        background: pkg.badgeColor || '#3b82f6',
                                        color: '#ffffff',
                                        fontSize: '0.72rem',
                                        fontWeight: '900',
                                        padding: '3px 10px',
                                        borderRadius: '10px',
                                        letterSpacing: '0.04em'
                                    }}>
                                        {pkg.badge}
                                    </div>
                                )}

                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        {pkg.contextualTag === 'css_design' && <LuPalette size={20} color="#c026d3" />}
                                        {pkg.contextualTag === 'seo' && <LuSearch size={20} color="#2563eb" />}
                                        {pkg.contextualTag === 'ecommerce_tax' && <LuShoppingCart size={20} color="#d97706" />}
                                        <h4 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a', fontWeight: '800' }}>
                                            {pkg.name}
                                        </h4>
                                    </div>

                                    <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '14px', lineHeight: 1.4 }}>
                                        {pkg.idealFor}
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '6px' }}>
                                        <span style={{ fontSize: '2.2rem', fontWeight: '900', color: '#0f172a' }}>
                                            {pkg.price}
                                        </span>
                                        <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                                            one-time flat sprint
                                        </span>
                                    </div>

                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: '#f1f5f9',
                                        color: '#475569',
                                        borderRadius: '6px',
                                        padding: '3px 8px',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        marginBottom: '16px'
                                    }}>
                                        <LuClock size={12} />
                                        <span>Turnaround: {pkg.turnaround}</span>
                                    </div>

                                    <p style={{ fontSize: '0.86rem', color: '#334155', lineHeight: 1.5, margin: '0 0 16px 0' }}>
                                        {pkg.description}
                                    </p>

                                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '14px', marginBottom: '18px' }}>
                                        <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: '800', color: '#94a3b8', marginBottom: '8px' }}>
                                            Key Deliverables
                                        </div>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {pkg.inclusions?.map((inc, i) => (
                                                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#334155' }}>
                                                    <LuCheck size={14} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                                                    <span>{inc}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => handleOpenBooking(pkg.id)}
                                    style={{
                                        width: '100%',
                                        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        fontWeight: '800',
                                        fontSize: '0.88rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        transition: 'all 0.15s ease',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <LuSparkles size={14} />
                                    <span>Book {pkg.name.split(' ')[0]} Sprint ({pkg.price})</span>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* TAB 2: WHITE-LABEL RESELLER CONFIGURATION (AGENCY SUPERUSER ONLY) */}
            {activeSubTab === 'reseller_config' && isSuperUser && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* General Agency Info */}
                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', color: '#0f172a', fontWeight: '800' }}>
                                    White-Label Agency Identity
                                </h4>
                                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                                    Resellers can brand all upsells, button labels, and booking funnels to their own design agency.
                                </p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#334155' }}>
                                    White-Label Mode:
                                </label>
                                <button
                                    type="button"
                                    onClick={() => updateUpsell({ resellerMode: !upsell.resellerMode })}
                                    style={{
                                        background: upsell.resellerMode ? '#10b981' : '#cbd5e1',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '20px',
                                        padding: '4px 14px',
                                        fontSize: '0.78rem',
                                        fontWeight: '800',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {upsell.resellerMode ? 'ON (Custom Agency)' : 'OFF (Mad Curious)'}
                                </button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                            <div>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    Agency / Reseller Name
                                </label>
                                <input
                                    type="text"
                                    value={upsell.agencyName}
                                    onChange={e => updateUpsell({ agencyName: e.target.value })}
                                    placeholder="e.g. Mad Curious or Studio Apex"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    Expert Professional Title
                                </label>
                                <input
                                    type="text"
                                    value={upsell.expertTitle}
                                    onChange={e => updateUpsell({ expertTitle: e.target.value })}
                                    placeholder="e.g. Cleverologist or Lead Designer"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    In-Dashboard Button Label
                                </label>
                                <input
                                    type="text"
                                    value={upsell.buttonLabel}
                                    onChange={e => updateUpsell({ buttonLabel: e.target.value })}
                                    placeholder="e.g. Hire a Cleverologist"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    Inquiry / Lead Routing Email
                                </label>
                                <input
                                    type="email"
                                    value={upsell.contactEmail}
                                    onChange={e => updateUpsell({ contactEmail: e.target.value })}
                                    placeholder="e.g. cleverologist@madcurious.com"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    Direct Booking / Calendar URL
                                </label>
                                <input
                                    type="url"
                                    value={upsell.bookingUrl}
                                    onChange={e => updateUpsell({ bookingUrl: e.target.value })}
                                    placeholder="e.g. https://calendly.com/your-agency or https://madcurious.com"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                />
                            </div>

                            <div>
                                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>
                                    Portfolio / Showcase URL
                                </label>
                                <input
                                    type="url"
                                    value={upsell.portfolioUrl}
                                    onChange={e => updateUpsell({ portfolioUrl: e.target.value })}
                                    placeholder="e.g. https://madcurious.com/"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.88rem' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contextual Triggers Rules */}
                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '24px' }}>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '1.1rem', color: '#0f172a', fontWeight: '800' }}>
                            Programmatic Contextual Triggers
                        </h4>
                        <p style={{ margin: '0 0 16px 0', fontSize: '0.82rem', color: '#64748b' }}>
                            Automatically prompt users with relevant service sprints when they encounter complex builder tasks.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '0.88rem', fontWeight: '700', color: '#0f172a' }}>Custom CSS / Design Editor</span>
                                    <input
                                        type="checkbox"
                                        checked={upsell.contextualTriggers?.cssTrigger}
                                        onChange={e => updateUpsell({
                                            contextualTriggers: { ...upsell.contextualTriggers, cssTrigger: e.target.checked }
                                        })}
                                    />
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    Prompts Site Polish Package ($199–$499) inside CSS overrides and layout editor.
                                </div>
                            </div>

                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '0.88rem', fontWeight: '700', color: '#0f172a' }}>SEO Audit & Settings</span>
                                    <input
                                        type="checkbox"
                                        checked={upsell.contextualTriggers?.seoTrigger}
                                        onChange={e => updateUpsell({
                                            contextualTriggers: { ...upsell.contextualTriggers, seoTrigger: e.target.checked }
                                        })}
                                    />
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    Prompts SEO & Speed Audit ($150) in SEO score widget and meta configuration.
                                </div>
                            </div>

                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '0.88rem', fontWeight: '700', color: '#0f172a' }}>Product Taxes & Migration</span>
                                    <input
                                        type="checkbox"
                                        checked={upsell.contextualTriggers?.taxTrigger}
                                        onChange={e => updateUpsell({
                                            contextualTriggers: { ...upsell.contextualTriggers, taxTrigger: e.target.checked }
                                        })}
                                    />
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    Prompts Store Migration ($299) in tax engines and store setup flows.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Reseller Sprint Pricing & Markup Editor */}
                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '24px' }}>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '1.1rem', color: '#0f172a', fontWeight: '800' }}>
                            Package Pricing, Descriptions & Reseller Markup
                        </h4>
                        <p style={{ margin: '0 0 16px 0', fontSize: '0.82rem', color: '#64748b' }}>
                            Adjust sprint prices and client billing margins directly.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {upsell.packages.map((pkg, idx) => (
                                <div
                                    key={pkg.id}
                                    style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        background: '#f8fafc',
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                        gap: '12px',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div>
                                        <label style={{ fontSize: '0.74rem', fontWeight: '700', color: '#64748b' }}>Package Name</label>
                                        <input
                                            type="text"
                                            value={pkg.name}
                                            onChange={e => updatePackage(idx, 'name', e.target.value)}
                                            style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.84rem' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.74rem', fontWeight: '700', color: '#64748b' }}>Client Price (Markup)</label>
                                        <input
                                            type="text"
                                            value={pkg.price}
                                            onChange={e => updatePackage(idx, 'price', e.target.value)}
                                            placeholder="e.g. $199–$499 or $350"
                                            style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.84rem' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.74rem', fontWeight: '700', color: '#64748b' }}>Turnaround</label>
                                        <input
                                            type="text"
                                            value={pkg.turnaround}
                                            onChange={e => updatePackage(idx, 'turnaround', e.target.value)}
                                            placeholder="e.g. 24 Hours"
                                            style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.84rem' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '16px' }}>
                                        <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#334155' }}>Enabled:</label>
                                        <input
                                            type="checkbox"
                                            checked={pkg.enabled}
                                            onChange={e => updatePackage(idx, 'enabled', e.target.checked)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: LEADS & SPRINT INQUIRIES (AGENCY SUPERUSER ONLY) */}
            {activeSubTab === 'leads' && isSuperUser && (
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', color: '#0f172a', fontWeight: '800' }}>
                                Captured Sprint Inquiries
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                                Client requests initiated via in-dashboard contextual buttons and the "Hire a Cleverologist" modal.
                            </p>
                        </div>
                        {upsell.leadsHistory?.length > 0 && (
                            <button
                                type="button"
                                onClick={() => updateUpsell({ leadsHistory: [] })}
                                style={{
                                    background: '#f1f5f9',
                                    color: '#64748b',
                                    border: '1px solid #cbd5e1',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.78rem',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                }}
                            >
                                Clear Leads History
                            </button>
                        )}
                    </div>

                    {(!upsell.leadsHistory || upsell.leadsHistory.length === 0) ? (
                        <div style={{ textAlign: 'center', padding: '36px 12px', color: '#94a3b8' }}>
                            <LuMail size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                            <div style={{ fontWeight: '700', fontSize: '0.92rem' }}>No Inquiries Captured Yet</div>
                            <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                                When clients click "Hire a Cleverologist" or contextual triggers in the builder, their project briefs will appear here.
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {upsell.leadsHistory.map((lead) => (
                                <div
                                    key={lead.id}
                                    style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '10px',
                                        padding: '14px 18px',
                                        background: '#f8fafc',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        flexWrap: 'wrap',
                                        gap: '12px'
                                    }}
                                >
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.92rem' }}>{lead.name}</span>
                                            <span style={{ fontSize: '0.78rem', color: '#2563eb', fontWeight: '700' }}>({lead.email})</span>
                                            <span style={{
                                                background: '#dbeafe',
                                                color: '#1e40af',
                                                fontSize: '0.7rem',
                                                fontWeight: '800',
                                                padding: '2px 8px',
                                                borderRadius: '8px'
                                            }}>
                                                {lead.packageName} ({lead.packagePrice})
                                            </span>
                                        </div>
                                        {lead.websiteUrl && (
                                            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>
                                                Site: <a href={lead.websiteUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>{lead.websiteUrl}</a>
                                            </div>
                                        )}
                                        {lead.notes && (
                                            <div style={{ fontSize: '0.82rem', color: '#334155', marginTop: '6px', fontStyle: 'italic', background: '#ffffff', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                "{lead.notes}"
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                        {new Date(lead.createdAt).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Render Booking Modal when open */}
            <AgencyBookingModal
                isOpen={isBookingModalOpen}
                onClose={() => setIsBookingModalOpen(false)}
                config={data}
                initialPackageId={modalPackageId}
                onSaveLead={handleSaveLeadFromModal}
            />
        </div>
    );
};

export default AgencyUpsellManager;
